import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GET_PROJECT_SOV_PER_MODEL } from '../../../query/project/stats/keys';
import { ChatType } from '../../../../types/monitor/ChatType';
import { MonitorPrompt } from '../../../../models/monitor/MonitorPrompt';
import {
  getProjectSovPerModel,
  ProjectSovPerModelPoint,
} from '../../../services/project/stats/ProjectDailyStatsService';

/**
 * Per-(model, brand) Share of Voice feeding the "Share of Voice by AI Chatbots" bar chart, computed
 * the same way as the calling page's own SoV pie / Brands ranking: per-prompt averaging (V2, the
 * default) or `sovMode: 'pooled'` (V1), where each column then equals the pie with that single model
 * picked in the filter.
 */
export const useGetProjectSovPerModel = (
  projectId: number | null,
  filteredMonitorPrompts: MonitorPrompt[] | null,
  chatType: ChatType | null,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  sovMode?: 'pooled'
) => {
  const queryClient = useQueryClient();
  const hasPrompts = !!filteredMonitorPrompts && filteredMonitorPrompts.length > 0;
  const queryEnabled = !!projectId && !!chatType && !!from && !!to && hasPrompts;

  const monitorPromptIds = useMemo(
    () => filteredMonitorPrompts?.map((p) => p.id).filter((id): id is number => id != null),
    [filteredMonitorPrompts]
  );

  const queryKey = [
    GET_PROJECT_SOV_PER_MODEL,
    projectId,
    chatType,
    from,
    to,
    // Key on the full prompts (not just ids) so a PENDING→ACTIVE status flip — same ids, changed
    // content — bumps the key and refetches on the 60s poll, mirroring useGetProjectDailyStats. Keying
    // on ids alone left this stuck on a cached-empty first result until a full page refresh. Request
    // still sends ids below.
    filteredMonitorPrompts,
    filterChatTypes,
    sovMode,
  ];

  const {
    data: sovPerModel,
    isLoading,
    isPlaceholderData,
    isError,
    error,
    refetch,
  } = useQuery<ProjectSovPerModelPoint[]>({
    queryKey,
    queryFn: () =>
      getProjectSovPerModel(
        projectId!,
        chatType!,
        from,
        to,
        filterChatTypes,
        monitorPromptIds,
        sovMode
      ),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
  });

  return {
    sovPerModel: queryEnabled ? sovPerModel ?? [] : [],
    isLoading: queryEnabled ? isLoading : false,
    // `keepPreviousData` keeps `isLoading` false after a key change (dates, filters) while the old
    // range is still on screen — `isLoading` alone makes the preview page look frozen, not loading.
    isRefreshing: queryEnabled ? isPlaceholderData : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () => queryClient.invalidateQueries({ queryKey, refetchType: 'active' }),
  };
};
